const REST_API_VERSION_HEADER = 'X-GDC-VERSION';
const LATEST_REST_API_VERSION = 5;
const POLL_DELAY_MS = 1000;

interface GoodDataRequestOptions {
  method?: string;
  body?: object;
}

const buildUrl = (domain: string, path: string) => {
  if (path.indexOf(domain) !== -1) {
    return path;
  }

  const trimmedDomain = domain.replace(/\/+$/, '');
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;

  return `${trimmedDomain}${normalizedPath}`;
};

const createRequestInit = (
  options: GoodDataRequestOptions = {}
): RequestInit => {
  const init: RequestInit = {
    method: options.method || 'GET',
    mode: 'cors',
    credentials: 'include',
    headers: {
      Accept: 'application/json; charset=utf-8',
      'Content-Type': 'application/json',
      [REST_API_VERSION_HEADER]: String(LATEST_REST_API_VERSION),
    },
  };

  if (options.body) {
    init.body = JSON.stringify(options.body);
  }

  return init;
};

const sleep = (ms: number) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

const goodDataRequest = async (
  domain: string,
  path: string,
  options: GoodDataRequestOptions = {}
): Promise<Response> => {
  const url = buildUrl(domain, path);
  const response = await fetch(url, createRequestInit(options));

  if (response.status === 202) {
    let pollUrl = url;

    if (response.headers.has('Location')) {
      pollUrl = response.headers.get('Location') as string;
    } else {
      const responseText = await response.text();

      try {
        const responseJson = JSON.parse(responseText);
        if (responseJson.uri) {
          pollUrl = responseJson.uri;
        }
      } catch (error) {
        // Response body is not JSON; keep polling on the original URL.
      }
    }

    await sleep(POLL_DELAY_MS);
    return goodDataRequest(domain, pollUrl, { method: 'GET' });
  }

  if (!response.ok) {
    throw new Error(
      `GoodData API request failed with status ${response.status}`
    );
  }

  return response;
};

export const loginSso = (
  domain: string,
  encryptedClaims: string,
  ssoProvider: string,
  targetUrl: string
): Promise<void> =>
  goodDataRequest(domain, '/gdc/account/customerlogin', {
    method: 'POST',
    body: {
      pgpLoginRequest: {
        targetUrl,
        ssoProvider,
        encryptedClaims,
      },
    },
  }).then(() => undefined);

export const getValidElements = (
  domain: string,
  projectId: string,
  attributeDisplayFormId: string
): Promise<any> =>
  goodDataRequest(
    domain,
    `/gdc/md/${projectId}/obj/${attributeDisplayFormId}/validElements`,
    {
      method: 'POST',
      body: {
        validElementsRequest: {},
      },
    }
  ).then((response) => response.json());
